/** Tab „Darstellung": Theme, Schriftgröße, Nachrichtenanzeige, gespeicherte Themes, eigenes CSS. */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { MessageDisplay, Theme, UserThemeDto } from '@lagerfeuer/shared';
import { Check, Monitor, Moon, Pencil, Sun, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ConfirmDialog } from '../../components/Modal';
import { errorMessage } from '../../lib/api';
import { sanitizeThemeVariables } from '../../lib/cssSanitizer';
import { usersApi } from '../../lib/endpoints';
import { qk } from '../../lib/queryClient';
import { useAppStore } from '../../store/app';
import { toast } from '../../store/ui';
import { CssSampleCard, CustomCssEditor } from './CustomCssEditor';
import { mergePreview, patchSettings, validHex } from './useSettings';

const THEME_OPTIONS: { id: Exclude<Theme, 'CUSTOM'>; label: string; hint: string; icon: typeof Moon }[] = [
  { id: 'DARK', label: 'Holzkohle', hint: 'Dunkel mit Glut-Akzenten', icon: Moon },
  { id: 'LIGHT', label: 'Tageslicht', hint: 'Hell und warm', icon: Sun },
  { id: 'SYSTEM', label: 'System', hint: 'Folgt dem Gerät', icon: Monitor },
];

export function AppearanceTab() {
  const settings = useAppStore((s) => s.settings);
  if (!settings) return null;
  return (
    <div className="lf-settings-sections">
      <section className="lf-settings-card stack" aria-labelledby="theme-heading">
        <h2 id="theme-heading">Theme</h2>
        <div className="lf-theme-options" role="radiogroup" aria-labelledby="theme-heading">
          {THEME_OPTIONS.map((o) => (
            <button
              key={o.id}
              type="button"
              role="radio"
              aria-checked={settings.theme === o.id}
              className="lf-theme-option"
              data-option={o.id}
              onClick={() => void patchSettings({ theme: o.id })}
              data-testid={`theme-option-${o.id}`}
            >
              <span className={`lf-theme-swatch lf-theme-swatch-${o.id.toLowerCase()}`} aria-hidden="true">
                <o.icon size={18} />
              </span>
              <span className="lf-theme-option-text">
                <strong>{o.label}</strong>
                <span className="small muted">{o.hint}</span>
              </span>
              {settings.theme === o.id && <Check size={18} aria-hidden="true" className="lf-theme-check" />}
            </button>
          ))}
        </div>
      </section>
      <section className="lf-settings-card stack" aria-labelledby="display-heading">
        <h2 id="display-heading">Anzeige</h2>
        <FontScale key={settings.fontScale} value={settings.fontScale} />
        <fieldset className="lf-fieldset">
          <legend className="label">Nachrichten</legend>
          {(['COZY', 'COMPACT'] as MessageDisplay[]).map((m) => (
            <label key={m} className="checkbox">
              <input
                type="radio"
                name="message-display"
                checked={settings.messageDisplay === m}
                onChange={() => void patchSettings({ messageDisplay: m })}
                data-testid={`message-display-${m}`}
              />
              <span>{m === 'COZY' ? 'Gemütlich – mit Avataren und Abständen' : 'Kompakt – mehr Nachrichten auf einen Blick'}</span>
            </label>
          ))}
        </fieldset>
        <label className="switch-row">
          <input type="checkbox" className="switch" checked={settings.compactMode} onChange={(e) => void patchSettings({ compactMode: e.target.checked })} data-testid="compact-mode" />
          <span>Kompakte Oberfläche (engere Listen und Abstände)</span>
        </label>
        <label className="switch-row">
          <input type="checkbox" className="switch" checked={settings.developerMode} onChange={(e) => void patchSettings({ developerMode: e.target.checked })} data-testid="developer-mode" />
          <span>Entwicklermodus (IDs kopieren im Kontextmenü)</span>
        </label>
      </section>
      <SavedThemes />
      <CustomCssEditor />
    </div>
  );
}

function FontScale({ value }: { value: number }) {
  const [v, setV] = useState(value);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  const change = (n: number) => {
    setV(n);
    mergePreview({ fontScale: n });
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      void patchSettings({ fontScale: n }).then(() => mergePreview({}, ['fontScale']));
    }, 500);
  };
  return (
    <div className="field">
      <label htmlFor="font-scale">Schriftgröße: {Math.round(v * 100)} %</label>
      <input id="font-scale" type="range" min={0.8} max={1.4} step={0.05} value={v} onChange={(e) => change(Number(e.target.value))} data-testid="font-scale" className="lf-range" />
    </div>
  );
}

/** Variablen, die der Theme-Editor per Farbwähler anbietet. */
const THEME_VARS: { key: string; label: string }[] = [
  { key: '--lf-accent', label: 'Akzent' },
  { key: '--lf-accent-strong', label: 'Akzent (kräftig)' },
  { key: '--lf-bg', label: 'Hintergrund außen' },
  { key: '--lf-bg-1', label: 'Seitenleiste' },
  { key: '--lf-bg-2', label: 'Chat-Fläche' },
  { key: '--lf-text', label: 'Text' },
  { key: '--lf-link', label: 'Links' },
];

function SavedThemes() {
  const qc = useQueryClient();
  const activeId = useAppStore((s) => s.settings?.activeThemeId ?? null);
  const themes = useQuery({ queryKey: qk.themes, queryFn: usersApi.themes });
  const [editing, setEditing] = useState<UserThemeDto | 'new' | null>(null);
  const [deleting, setDeleting] = useState<UserThemeDto | null>(null);

  const remove = async (t: UserThemeDto) => {
    try {
      await usersApi.deleteTheme(t.id);
      qc.setQueryData<UserThemeDto[]>(qk.themes, (old) => old?.filter((x) => x.id !== t.id));
      if (activeId === t.id)
        useAppStore.getState().update((d) => {
          if (d.settings) d.settings.activeThemeId = null;
        });
    } catch (e) {
      toast.error('Theme konnte nicht gelöscht werden', errorMessage(e));
    }
    setDeleting(null);
  };

  return (
    <section className="lf-settings-card stack" aria-labelledby="themes-heading" data-testid="saved-themes">
      <div className="row">
        <h2 id="themes-heading">Eigene Themes</h2>
        <span className="spacer" />
        {!editing && (
          <button type="button" className="btn btn-sm" onClick={() => setEditing('new')} data-testid="theme-new">
            Neues Theme
          </button>
        )}
      </div>
      {themes.isError && <p className="error-text">{errorMessage(themes.error)}</p>}
      {!themes.isLoading && (themes.data ?? []).length === 0 && !editing && <p className="small muted">Noch keine eigenen Themes gespeichert.</p>}
      <ul className="lf-theme-list">
        {(themes.data ?? []).map((t) => (
          <li key={t.id} className="lf-theme-item" data-testid="theme-item" data-active={t.id === activeId || undefined}>
            <span className="lf-theme-dots" aria-hidden="true">
              {['--lf-accent', '--lf-bg-2', '--lf-text'].map((k) => (
                <span key={k} style={{ background: validHex(t.variables[k]) ?? 'var(--lf-bg-4)' }} />
              ))}
            </span>
            <span className="lf-theme-name truncate">{t.name}</span>
            {t.id === activeId && <span className="tag">Aktiv</span>}
            <span className="spacer" />
            {t.id === activeId ? (
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => void patchSettings({ activeThemeId: null })} data-testid="theme-deactivate">
                Deaktivieren
              </button>
            ) : (
              <button type="button" className="btn btn-sm" onClick={() => void patchSettings({ activeThemeId: t.id })} data-testid="theme-activate">
                Anwenden
              </button>
            )}
            <button type="button" className="icon-btn" aria-label={`${t.name} bearbeiten`} onClick={() => setEditing(t)} data-testid="theme-edit">
              <Pencil size={16} />
            </button>
            <button type="button" className="icon-btn" aria-label={`${t.name} löschen`} onClick={() => setDeleting(t)} data-testid="theme-delete">
              <Trash2 size={16} />
            </button>
          </li>
        ))}
      </ul>
      {editing && <ThemeEditor key={editing === 'new' ? 'new' : editing.id} theme={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />}
      {deleting && (
        <ConfirmDialog
          title="Theme löschen?"
          message={`„${deleting.name}" wird gelöscht.`}
          confirmLabel="Löschen"
          danger
          onClose={() => setDeleting(null)}
          onConfirm={() => void remove(deleting)}
        />
      )}
    </section>
  );
}

function ThemeEditor({ theme, onDone }: { theme: UserThemeDto | null; onDone: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(theme?.name ?? '');
  const [vars, setVars] = useState<Record<string, string>>(theme?.variables ?? {});
  const [css, setCss] = useState(theme?.css ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    mergePreview({ variables: sanitizeThemeVariables(vars) });
  }, [vars]);
  useEffect(() => () => mergePreview({}, ['variables']), []);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    const body = { name: name.trim(), css, variables: sanitizeThemeVariables(vars) };
    try {
      const res = theme ? await usersApi.updateTheme(theme.id, body) : await usersApi.createTheme(body);
      qc.setQueryData<UserThemeDto[]>(qk.themes, (old) => (theme ? (old ?? []).map((x) => (x.id === res.id ? res : x)) : [...(old ?? []), res]));
      toast.success(theme ? 'Theme gespeichert' : 'Theme angelegt');
      onDone();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <form className="lf-theme-editor stack" onSubmit={save} data-testid="theme-editor">
      <div className="field">
        <label htmlFor="theme-name">Name</label>
        <input id="theme-name" className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={64} required data-testid="theme-name" />
      </div>
      <div className="lf-theme-vars">
        {THEME_VARS.map((v) => (
          <label key={v.key} className="lf-theme-var">
            <input
              type="color"
              className="lf-color-input"
              value={validHex(vars[v.key]) ?? '#808080'}
              onChange={(e) => setVars((old) => ({ ...old, [v.key]: e.target.value }))}
              data-testid={`theme-var-${v.key.slice(5)}`}
            />
            <span className="small">{v.label}</span>
            {vars[v.key] && (
              <button
                type="button"
                className="btn-link small"
                onClick={() =>
                  setVars((old) => {
                    const { [v.key]: _x, ...rest } = old;
                    return rest;
                  })
                }
              >
                Standard
              </button>
            )}
          </label>
        ))}
      </div>
      <div className="field">
        <label htmlFor="theme-css">Zusätzliches CSS (optional)</label>
        <textarea id="theme-css" className="textarea lf-code-input" rows={5} value={css} onChange={(e) => setCss(e.target.value)} spellCheck={false} data-testid="theme-css" />
      </div>
      <CssSampleCard testId="theme-preview" />
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      <div className="row">
        <span className="small muted">Farben werden live in der App gezeigt.</span>
        <span className="spacer" />
        <button type="button" className="btn btn-ghost" onClick={onDone} data-testid="theme-cancel">
          Abbrechen
        </button>
        <button type="submit" className="btn btn-primary" disabled={busy || !name.trim()} data-testid="theme-save">
          Speichern
        </button>
      </div>
    </form>
  );
}
